import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Globe, ChevronDown, Shield, Smartphone } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { pageContainer, fadeInUp, tapScale } from '@/lib/motion'
import { useAuthStore } from '@/stores/authStore'

const languages = [
  { code: 'zh-TW', label: '繁體中文', flag: '🇹🇼' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
]

// Telegram-style SVG icon
function TelegramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  )
}

// Google "G" icon
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

// Apple icon
function AppleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  )
}

// Phone icon wrapper (uses lucide Smartphone)
function PhoneIcon({ className }: { className?: string }) {
  return <Smartphone className={className} />
}

export function LoginScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string })?.from || '/'
  const [showLangMenu, setShowLangMenu] = useState(false)
  const [selectedLang, setSelectedLang] = useState(languages[0])
  const [isLoading, setIsLoading] = useState<string | null>(null)

  const login = useAuthStore((s) => s.login)

  const handleLogin = (provider: string) => {
    setIsLoading(provider)
    // Simulate login delay
    setTimeout(() => {
      login(provider)
      setIsLoading(null)
      navigate(from, { replace: true })
    }, 1500)
  }

  const t = selectedLang.code === 'zh-TW' ? {
    tagline: '專屬德州撲克俱樂部',
    subtitle: '在 Telegram 上暢玩真金德州撲克',
    telegramLogin: '使用 Telegram 登入',
    orDivider: '或使用其他方式登入',
    google: 'Google 登入',
    apple: 'Apple 登入',
    phone: '手機驗證',
    terms: '登入即表示您同意我們的',
    termsLink: '服務條款',
    and: '和',
    privacyLink: '隱私政策',
    secureNote: '安全加密連線',
  } : {
    tagline: 'Exclusive Poker Club',
    subtitle: 'Play real-money Texas Poker on Telegram',
    telegramLogin: 'Sign in with Telegram',
    orDivider: 'or sign in with',
    google: 'Google',
    apple: 'Apple',
    phone: 'Phone OTP',
    terms: 'By signing in, you agree to our',
    termsLink: 'Terms of Service',
    and: 'and',
    privacyLink: 'Privacy Policy',
    secureNote: 'Secure encrypted connection',
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={pageContainer}
      className="flex flex-col h-full bg-background relative overflow-hidden"
    >
      {/* Ambient Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[500px] gradient-radial-gold pointer-events-none" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[400px] h-[300px] gradient-radial-gold opacity-30 pointer-events-none" />
      <div className="fixed inset-0 noise-overlay pointer-events-none" />

      {/* Language Selector */}
      <motion.div variants={fadeInUp} className="relative z-20 flex justify-end px-6 pt-6 safe-area-top">
        <button
          onClick={() => setShowLangMenu(!showLangMenu)}
          className="flex items-center gap-2 px-3 py-2 rounded-full glass-card text-sm"
        >
          <Globe className="w-4 h-4 text-muted-foreground" />
          <span className="text-foreground font-medium">{selectedLang.flag} {selectedLang.label}</span>
          <ChevronDown className={cn(
            "w-3 h-3 text-muted-foreground transition-transform duration-200",
            showLangMenu && "rotate-180"
          )} />
        </button>

        <AnimatePresence>
          {showLangMenu && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full right-6 mt-2 w-48 rounded-2xl glass-card-elevated overflow-hidden z-30"
            >
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setSelectedLang(lang)
                    setShowLangMenu(false)
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-200",
                    selectedLang.code === lang.code
                      ? "bg-gold/10 text-gold"
                      : "text-foreground hover:bg-foreground/5"
                  )}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="font-medium">{lang.label}</span>
                  {selectedLang.code === lang.code && (
                    <span className="ml-auto text-gold">✓</span>
                  )}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Logo & Branding */}
      <motion.div variants={fadeInUp} className="flex-1 flex flex-col items-center justify-center px-8 relative z-10 -mt-8">
        {/* Logo Mark — layered for independent rotation */}
        <motion.div variants={fadeInUp} className="relative mb-10 w-[112px] h-[112px]">
          {/* Shared SVG defs */}
          <svg width="0" height="0" className="absolute">
            <defs>
              <linearGradient id="chipGold" x1="0" y1="0" x2="112" y2="112" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#F5D78E" />
                <stop offset="40%" stopColor="#D4A843" />
                <stop offset="100%" stopColor="#A67C2E" />
              </linearGradient>
              <linearGradient id="chipGoldLight" x1="0" y1="0" x2="112" y2="112" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#F5D78E" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#D4A843" stopOpacity="0.2" />
              </linearGradient>
              <linearGradient id="chipInner" x1="20" y1="20" x2="92" y2="92" gradientUnits="userSpaceOnUse">
                <stop offset="0%" style={{ stopColor: 'var(--chip-inner-from)' }} />
                <stop offset="100%" style={{ stopColor: 'var(--chip-inner-to)' }} />
              </linearGradient>
              <linearGradient id="textGold" x1="36" y1="32" x2="76" y2="80" gradientUnits="userSpaceOnUse">
                <stop offset="0%" style={{ stopColor: 'var(--shimmer-highlight)' }} />
                <stop offset="50%" style={{ stopColor: 'var(--shimmer-base)' }} />
                <stop offset="100%" style={{ stopColor: 'var(--shimmer-base)' }} />
              </linearGradient>
              <radialGradient id="innerShine" cx="44" cy="40" r="40" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.04" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </radialGradient>
            </defs>
          </svg>

          {/* Layer 1: Outer ring + notches — slow clockwise */}
          <motion.svg
            className="absolute inset-0"
            width="112" height="112" viewBox="0 0 112 112" fill="none"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 80, repeat: Infinity, ease: 'linear' }}
          >
            <circle cx="56" cy="56" r="55" stroke="url(#chipGoldLight)" strokeWidth="0.5" fill="none" />
            <circle cx="56" cy="56" r="52" stroke="url(#chipGold)" strokeWidth="2" fill="none" />
            {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle) => (
              <rect
                key={angle}
                x="54.5" y="1" width="3" height="7" rx="1.5"
                fill="url(#chipGold)" opacity="0.5"
                transform={`rotate(${angle} 56 56)`}
              />
            ))}
          </motion.svg>

          {/* Layer 2: Inner ring + diamond accents — slow counter-clockwise */}
          <motion.svg
            className="absolute inset-0"
            width="112" height="112" viewBox="0 0 112 112" fill="none"
            animate={{ rotate: [0, -360] }}
            transition={{ duration: 120, repeat: Infinity, ease: 'linear' }}
          >
            <circle cx="56" cy="56" r="42" stroke="url(#chipGold)" strokeWidth="1.5" fill="url(#chipInner)" />
            <circle cx="56" cy="56" r="42" fill="url(#innerShine)" />
            <circle cx="56" cy="56" r="33" stroke="url(#chipGold)" strokeWidth="0.5" fill="none" opacity="0.25" />
            {[0, 90, 180, 270].map((angle) => (
              <rect
                key={`d-${angle}`}
                x="55" y="25" width="2" height="2" rx="0.3"
                fill="url(#chipGold)" opacity="0.35"
                transform={`rotate(${angle} 56 56)`}
              />
            ))}
          </motion.svg>

          {/* Layer 3: "P" monogram — static */}
          <svg
            className="absolute inset-0"
            width="112" height="112" viewBox="0 0 112 112" fill="none"
          >
            <text
              x="56" y="56"
              textAnchor="middle" dominantBaseline="central"
              fill="url(#textGold)"
              fontFamily="'Playfair Display', 'Georgia', 'Times New Roman', serif"
              fontSize="42" fontWeight="600" letterSpacing="-1"
            >
              P
            </text>
          </svg>

          {/* Glow behind logo — breathing pulse */}
          <motion.div
            className="absolute inset-0 bg-gold/15 blur-[40px] rounded-full -z-10 scale-[1.8]"
            animate={{ opacity: [0.4, 0.8, 0.4], scale: [1.7, 1.9, 1.7] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>

        {/* Brand text — gold shimmer sweep via animated gradient */}
        <motion.h1 variants={fadeInUp} className="text-[28px] font-display font-semibold tracking-[0.12em] mb-2 uppercase">
          <motion.span
            className="inline-block bg-clip-text text-transparent"
            style={{
              backgroundImage: 'linear-gradient(90deg, var(--shimmer-base) 0%, var(--shimmer-base) 35%, var(--shimmer-highlight) 50%, var(--shimmer-base) 65%, var(--shimmer-base) 100%)',
              backgroundSize: '200% 100%',
            }}
            animate={{ backgroundPosition: ['100% 0%', '-100% 0%'] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
          >
            PWG Lounge
          </motion.span>
        </motion.h1>
        <motion.p variants={fadeInUp} className="text-[15px] text-muted-foreground/70 font-display tracking-wide mb-1">
          {t.tagline}
        </motion.p>
        <motion.p variants={fadeInUp} className="text-xs text-muted-foreground/40 tracking-wider mb-10">
          {t.subtitle}
        </motion.p>

        {/* Login Buttons */}
        <motion.div variants={fadeInUp} className="w-full max-w-sm space-y-4">
          {/* Telegram - Primary CTA */}
          <motion.button
            whileTap={tapScale}
            onClick={() => handleLogin('telegram')}
            disabled={isLoading !== null}
            className={cn(
              "w-full h-14 rounded-2xl font-bold text-base tracking-wide transition-all duration-200 flex items-center justify-center gap-3",
              "bg-[#2AABEE] hover:bg-[#35b8f7] text-white",
              "shadow-[0_4px_16px_-4px_rgba(42,171,238,0.5),0_8px_24px_-8px_rgba(42,171,238,0.3)]",
              isLoading === 'telegram' && "opacity-70"
            )}
          >
            {isLoading === 'telegram' ? (
              <div className="w-5 h-5 border-2 border-foreground/30 border-t-foreground rounded-full animate-spin" />
            ) : (
              <>
                <TelegramIcon className="w-6 h-6" />
                {t.telegramLogin}
              </>
            )}
          </motion.button>

          {/* Divider */}
          <div className="flex items-center gap-4 py-1">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-foreground/10 to-transparent" />
            <span className="text-xs text-muted-foreground/60 font-medium uppercase tracking-wider">{t.orDivider}</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-foreground/10 to-transparent" />
          </div>

          {/* Social Login Row */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { id: 'google', icon: GoogleIcon, label: t.google, iconClass: 'w-5 h-5' },
              { id: 'apple', icon: AppleIcon, label: t.apple, iconClass: 'w-5 h-5 text-foreground' },
              { id: 'phone', icon: PhoneIcon, label: t.phone, iconClass: 'w-5 h-5 text-emerald-400' },
            ].map((provider) => (
              <motion.button
                key={provider.id}
                whileTap={tapScale}
                onClick={() => handleLogin(provider.id)}
                disabled={isLoading !== null}
                className={cn(
                  "flex flex-col items-center gap-2 py-4 rounded-2xl glass-card hover:border-foreground/20 transition-all duration-200",
                  isLoading === provider.id && "opacity-70"
                )}
              >
                {isLoading === provider.id ? (
                  <div className="w-5 h-5 border-2 border-foreground/30 border-t-gold rounded-full animate-spin" />
                ) : (
                  <provider.icon className={provider.iconClass} />
                )}
                <span className="text-[11px] font-semibold text-muted-foreground">{provider.label}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* Bottom - Terms & Security */}
      <motion.div variants={fadeInUp} className="relative z-10 px-8 pb-8 safe-area-bottom space-y-3">
        {/* Security badge */}
        <div className="flex items-center justify-center gap-2 text-muted-foreground/40">
          <Shield className="w-3.5 h-3.5" />
          <span className="text-[11px] font-medium">{t.secureNote}</span>
        </div>

        {/* Terms */}
        <p className="text-center text-[11px] text-muted-foreground/40 leading-relaxed">
          {t.terms}{' '}
          <button className="text-gold/60 hover:text-gold underline underline-offset-2">{t.termsLink}</button>
          {' '}{t.and}{' '}
          <button className="text-gold/60 hover:text-gold underline underline-offset-2">{t.privacyLink}</button>
        </p>
      </motion.div>
    </motion.div>
  )
}
